import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const CarrickfergusAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Carrickfergus | Castle Town Stress Relief</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Carrickfergus from Belfast clinic (14 miles). Evidence-based treatment for tourism stress, performance anxiety, panic. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Carrickfergus, stress management Carrickfergus, hypnotherapy Carrickfergus anxiety, performance anxiety tour guides, hospitality stress Carrickfergus" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/carrickfergus-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Carrickfergus | Castle Town Stress Relief" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Carrickfergus from Belfast. Evidence-based treatment for tourism industry stress, performance anxiety." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Carrickfergus",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Carrickfergus from Belfast clinic. Specializing in tourism industry stress, performance anxiety, seasonal employment anxiety, and hospitality pressures.",
            "url": "https://www.sharonclare-hypnotherapy.com/carrickfergus-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Carrickfergus",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Belfast from Carrickfergus for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is just 14 miles from Carrickfergus via the M5 and M2 into Belfast, then south to the Ormeau Road, taking only 25-30 minutes by car. This very short distance makes regular anxiety treatment appointments extremely convenient. Carrickfergus is one of the closest towns to our Belfast clinic, making specialist care highly accessible for weekly or fortnightly sessions."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with tourism industry performance anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for performance anxiety, particularly for tour guides, castle staff, and hospitality workers. Many Carrickfergus tourism workers experience anxiety about public speaking, dealing with difficult visitors, or performing their role confidently. Solution-focused hypnotherapy builds confidence, reduces anticipatory anxiety, and helps you feel calm and capable in your role."
                }
              },
              {
                "@type": "Question",
                "name": "Do you work with seasonal employment anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, seasonal employment creates unique stressors - financial uncertainty during off-seasons, pressure during busy periods, job insecurity, and the challenge of managing inconsistent income. Hypnotherapy helps by reducing anxiety around uncertainty, building resilience and coping strategies, and addressing the stress that comes from seasonal work patterns common in Carrickfergus's tourism industry."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with hospitality customer service stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Working in hospitality means constant customer interaction, dealing with complaints, managing difficult situations, and maintaining a professional demeanor even when stressed. Hypnotherapy helps you develop better stress management, build emotional resilience, set healthy boundaries, and prevent customer service pressures from following you home and affecting your wellbeing."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on individual circumstances and goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and commitment to the process."
                }
              },
              {
                "@type": "Question",
                "name": "Is the journey from Carrickfergus to Belfast easy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, very easy. At just 14 miles, Carrickfergus is one of the most conveniently located towns for accessing our Belfast clinic. You can take the M5 and M2 into Belfast, then head south along the Ormeau Road (A24). The journey takes only 25-30 minutes, making it practical to attend sessions before or after work, or to fit appointments into your schedule without consuming large amounts of time."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Carrickfergus
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Carrickfergus from our convenient Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">14 miles</span> from Carrickfergus via M5/M2 • <span className="font-semibold">25-30 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for maximum flexibility</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Carrickfergus */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Carrickfergus
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Carrickfergus and struggling with anxiety or stress, you're not alone. As a historic castle town with a population of around 28,000, Carrickfergus has a unique character shaped by its tourism industry, maritime heritage, and close proximity to Belfast. While the town's beautiful setting and rich history attract visitors from around the world, working and living in a tourism-dependent community brings its own psychological challenges.
              </p>
              <p>
                Many Carrickfergus residents work in the tourism and hospitality sector - as tour guides at the iconic Norman castle, in restaurants and cafes serving visitors, in retail catering to tourists, or in seasonal roles that fluctuate with visitor numbers. This type of work often involves performance anxiety (speaking to groups, dealing with difficult customers), seasonal employment stress (financial uncertainty during quiet months), and the emotional labor of maintaining a professional, welcoming demeanor even when you're stressed or anxious yourself.
              </p>
              <p>
                While I don't have a physical clinic in Carrickfergus itself, I'm proud to serve the Carrickfergus community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast. The journey from Carrickfergus is exceptionally convenient - just 14 miles, taking the M5 and M2 into Belfast and then heading south to the Ormeau Road, a drive of only 25-30 minutes. Carrickfergus is one of the closest towns to our Belfast clinic, making regular anxiety treatment appointments highly practical and accessible. For those who prefer the convenience of online sessions, I also offer secure video call appointments from the comfort of your home.
              </p>
              <p>
                As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand the specific stressors that Carrickfergus residents face. Tourism industry anxiety, performance pressures, seasonal employment insecurity, customer service stress, and the challenge of managing financial uncertainty during off-peak months - these are real issues that affect mental health and quality of life. Solution-focused hypnotherapy offers an evidence-based approach that addresses these challenges by calming your nervous system, building resilience, and helping you develop healthier responses to stress.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Carrickfergus Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Performance anxiety before giving tours or presentations',
                    'Anticipatory worry about difficult customers or situations',
                    'Physical symptoms: racing heart, tight chest, shaky hands',
                    'Difficulty sleeping before busy shifts or peak season',
                    'Constant worry about job security or seasonal income',
                    'Social anxiety in customer-facing roles',
                    'Panic attacks triggered by work situations',
                    'Difficulty switching off after stressful interactions'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Tourism Industry Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'Public speaking anxiety for tour guides',
                    'Customer service pressures and complaints',
                    'Seasonal employment uncertainty and income gaps',
                    'Financial stress during off-peak months',
                    'Performance pressure during busy tourist season',
                    'Emotional labor of always being "on"',
                    'Dealing with difficult or demanding visitors',
                    'Work-life balance challenges during peak season'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                If anxiety is affecting your work performance, sleep, relationships, or quality of life, you don't have to struggle alone. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety, helping you feel calm, confident, and in control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from worry and toward practical, positive change. Hypnosis calms the overactive fight-or-flight response, reduces performance anxiety, and helps you feel confident and capable in challenging situations.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms. Your body learns to stay calm even in challenging customer interactions or performance situations.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Build Confidence',
                  text: 'Develop genuine confidence in your abilities, whether giving tours, handling difficult customers, or managing uncertainty. Replace anxious thinking with calm, capable responses.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Improve Sleep Quality',
                  text: 'Stop work stress from following you home. Quiet the racing mind and restore restful sleep, even during busy tourism seasons or when facing financial uncertainty.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Performance anxiety (tour guides, public speaking)',
                    'Social anxiety and customer service stress',
                    'Panic attacks and acute anxiety',
                    'Generalized worry and chronic stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Financial anxiety and seasonal employment stress',
                    'Sleep problems and racing thoughts',
                    'Work-life balance and emotional exhaustion',
                    'Confidence and self-esteem issues'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Choose Sharon Clare Hypnotherapy for Carrickfergus?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  CNHC Registered Professional Care
                </h3>
                <p className="text-gray-700">
                  As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. You can trust that you're receiving evidence-based treatment from a qualified professional who maintains strict ethical standards and approaches your care with clinical professionalism.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Tourism Industry Stress
                </h3>
                <p className="text-gray-700">
                  I understand the unique pressures of tourism and hospitality work - performance anxiety, seasonal employment stress, customer service pressures, and financial uncertainty during quiet months. Having worked with many tourism industry professionals, I'm sensitive to the specific challenges you face and tailor treatment to address these real-world stressors.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Extremely Convenient from Carrickfergus
                </h3>
                <p className="text-gray-700">
                  At just 14 miles (25-30 minutes drive), Carrickfergus is one of the most conveniently located towns for accessing our Belfast clinic. The short journey via the M5 and M2 makes regular weekly or fortnightly sessions highly practical. You can easily fit appointments before or after work without the journey consuming significant time.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Flexible Session Options
                </h3>
                <p className="text-gray-700">
                  Choose between in-person sessions at our Belfast clinic or secure online sessions via video call. Online sessions provide the same professional anxiety treatment from home - ideal for shift workers, those with irregular tourism season schedules, or anyone who prefers the convenience of online therapy without travel time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting to Belfast from Carrickfergus */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Carrickfergus
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Recommended)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M5 and M2 into Belfast, then head south along the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> 14 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 25-30 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">By public transport:</span> Botanic and City Hospital are the nearest train stations, with Lanyon Place serving regional routes, and Metro buses run along the Ormeau Road corridor
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Extremely Convenient Access</h3>
                <p className="text-gray-700">
                  At just 14 miles and 25-30 minutes from Carrickfergus, our Belfast clinic is exceptionally accessible for regular anxiety treatment. This is one of the shortest journeys from any town to our clinic, making it practical to attend sessions before or after work, or to fit appointments into your schedule without consuming large amounts of time. The convenient proximity means you get specialist care without significant travel commitment.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-accent-200">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer attending from home? I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional anxiety treatment with the convenience of attending from your home in Carrickfergus. This option is ideal for shift workers with irregular hours, those with caring responsibilities, or anyone who prefers the flexibility of online therapy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Carrickfergus Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I work as a tour guide at Carrickfergus Castle and I was having terrible performance anxiety every time I had to speak to groups. My hands would shake, my voice would wobble, and I dreaded every tour. Sharon's hypnotherapy helped me build real confidence. After five sessions, I actually enjoy giving tours now. The anxiety is gone and I feel capable and calm even with large groups. The drive to Belfast was so short it was no problem at all."
                </p>
                <p className="font-semibold text-gray-900">T.B., Carrickfergus - Performance Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in hospitality, I was constantly stressed by difficult customers and the seasonal uncertainty of my job. I'd worry about money during quiet months and feel overwhelmed during busy periods. Sharon helped me manage the anxiety and build resilience. I'm still in the same job with the same challenges, but I handle everything so much better now. I sleep properly and don't take the stress home with me anymore."
                </p>
                <p className="font-semibold text-gray-900">K.M., Carrickfergus - Customer Service Stress and Seasonal Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was having panic attacks, especially before busy shifts during tourist season. The physical symptoms were terrifying - racing heart, couldn't breathe, felt like I was going to collapse. After hypnotherapy with Sharon, the panic attacks have stopped completely. I learned techniques to manage the anxiety before it escalates. The 15-minute drive from Carrickfergus to Belfast was easy and worth every minute for the peace of mind I've gained."
                </p>
                <p className="font-semibold text-gray-900">L.S., Carrickfergus - Panic Attacks and Work Anxiety</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Carrickfergus Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Carrickfergus for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Belfast is just 14 miles from Carrickfergus via the M5 and M2 into Belfast, then south to the Ormeau Road, taking only 25-30 minutes by car. This very short distance makes regular anxiety treatment appointments extremely convenient. Carrickfergus is one of the closest towns to our Belfast clinic, making specialist care highly accessible for weekly or fortnightly sessions.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with tourism industry performance anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for performance anxiety, particularly for tour guides, castle staff, and hospitality workers. Many Carrickfergus tourism workers experience anxiety about public speaking, dealing with difficult visitors, or performing their role confidently. Solution-focused hypnotherapy builds confidence, reduces anticipatory anxiety, and helps you feel calm and capable in your role.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you work with seasonal employment anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, seasonal employment creates unique stressors - financial uncertainty during off-seasons, pressure during busy periods, job insecurity, and the challenge of managing inconsistent income. Hypnotherapy helps by reducing anxiety around uncertainty, building resilience and coping strategies, and addressing the stress that comes from seasonal work patterns common in Carrickfergus's tourism industry.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with hospitality customer service stress?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Working in hospitality means constant customer interaction, dealing with complaints, managing difficult situations, and maintaining a professional demeanor even when stressed. Hypnotherapy helps you develop better stress management, build emotional resilience, set healthy boundaries, and prevent customer service pressures from following you home and affecting your wellbeing.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for anxiety?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on individual circumstances and goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and commitment to the process.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is the journey from Carrickfergus to Belfast easy?
                </h3>
                <p className="text-gray-700">
                  Yes, very easy. At just 14 miles, Carrickfergus is one of the most conveniently located towns for accessing our Belfast clinic. You can take the M5 and M2 into Belfast, then head south along the Ormeau Road (A24). The journey takes only 25-30 minutes, making it practical to attend sessions before or after work, or to fit appointments into your schedule without consuming large amounts of time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Calm and Confidence"
        subtitle="Take the first step toward less anxiety, better performance, and more peace. Book a free consultation today and discover how hypnotherapy can help you feel calm, confident, and in control."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default CarrickfergusAnxietyStress;
